import { type ReadonlySignal, type Signal, signal } from "@preact/signals-core";
import type { CombatMarks } from "../islands/map_combat/marks";
import type { LiveToken, LiveTokenPreview } from "../types/generated/live";

// The tokens still to be taken into a fight: the ones a master may tick, and the ones ticked.
export type Ticks = { eligible: string[]; ticked: string[] };

// What the Combate panel's island asks of the board it sits on. The board's `map-combat` controller sets it once the board is
// ready and clears it when it goes; the panel never reaches into the board any other way.
export type CombatBridge = {
	tokens: ReadonlySignal<Record<string, LiveToken>>;
	selection: ReadonlySignal<string[]>;
	// The board's own channel refused this viewer: nothing the panel holds is to be kept.
	rejected: ReadonlySignal<boolean>;
	// Whether the panel is showing.
	open: ReadonlySignal<boolean>;
	// Tick mode: null while the board is not asking which monsters join. A tap on an eligible token toggles it.
	ticks: Signal<Ticks | null>;
	// What the board draws of the combatants on its tokens, by token id; the island sets it from the encounter's live client.
	marks: Signal<CombatMarks>;
	// The token whose monster's sheet the board asks the panel to open; the island clears it once it has.
	sheetFor: Signal<string | null>;
	// Opens the panel on that token's monster, as the token menu's Abrir ficha aqui asks.
	openSheet: (id: string) => void;
	// Enters tick mode with the monsters that may join, after putting down whatever tool the board held.
	startTicks: (eligible: string[]) => void;
	select: (id: string | null) => void;
	reveal: (id: string) => void;
	remove: (id: string) => void;
	// A monster from the Peças search, hidden, beside the monsters already there.
	place: (subject: LiveTokenPreview) => void;
	close: () => void;
	toast: (message: string) => void;
};

export const combatBridge = signal<CombatBridge | null>(null);

// Up to this many monsters on the map, Iniciar combate starts with all of them ticked; above it, with none.
const ALL_TICKED_UP_TO = 6;

export const initialTicks = (eligible: string[]): Ticks => ({ eligible, ticked: eligible.length <= ALL_TICKED_UP_TO ? eligible : [] });

export const toggled = (ticks: Ticks, id: string): Ticks => {
	if (!ticks.eligible.includes(id)) return ticks;
	return { ...ticks, ticked: ticks.ticked.includes(id) ? ticks.ticked.filter((other) => other !== id) : [...ticks.ticked, id] };
};
